import { For, Grid, GridItem } from '@chakra-ui/react';
import {
  SensorOrSubSensorInfoWithoutSection,
  SensorOrSubSensorTypeEnum,
} from '@components/cockpit/panels/monitoring/monitoring.types';
import { SpecificCommands } from '@components/map/popup/infos/sensors/command/Commands';
import SpecificMaintenanceActions from '@components/map/popup/infos/sensors/maintenance/actions/SpecificMaintenanceActions';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasSensorRoleFromActiveProfile } from '@redux/authent/authent.selectors';
import { sensorFamilyFromSensorType } from '@utils/sensors/configuration.constants';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { ActionEnum } from '@/types/commons/commons.types';

import { SensorSection, SensorSectionFactory } from '../../GlobalTab';

function MaintenanceAndCommandSection(
  sensorOrSubSensorInfo: Readonly<SensorOrSubSensorInfoWithoutSection>,
): SensorSection {
  const sensorActionMaintain = {
    action: ActionEnum.MAINTAIN,
    sensorFamily: sensorFamilyFromSensorType[sensorOrSubSensorInfo.type],
  };
  const sensorActionCommand = {
    action: ActionEnum.COMMAND,
    sensorFamily: sensorFamilyFromSensorType[sensorOrSubSensorInfo.type],
  };

  const canDoSensorMaintenance = useAppSelector((state) => hasSensorRoleFromActiveProfile(state, sensorActionMaintain));
  const canDoSensorCommands = useAppSelector((state) => hasSensorRoleFromActiveProfile(state, sensorActionCommand));

  const specificMaintenanceActions = SpecificMaintenanceActions({ sensorOrSubSensorInfo });
  const specificCommandActions = SpecificCommands({ sensorOrSubSensorInfo });

  const canShowMaintenanceActions = canDoSensorMaintenance && specificMaintenanceActions !== null;
  const canShowCommandActions = canDoSensorCommands && specificCommandActions !== null;

  const gridRef = useRef<HTMLDivElement>(null);
  const [itemCount, setItemCount] = useState(0);
  const fillerKeys = useMemo(
    () => Array.from({ length: (3 - (itemCount % 3)) % 3 }, (_, i) => `filler-${i}`),
    [itemCount],
  );

  useLayoutEffect(() => {
    if (!gridRef.current) return;

    itemCount === 0 && setItemCount(gridRef.current.children.length);
  }, [specificMaintenanceActions, specificCommandActions, itemCount]);

  if (!canShowMaintenanceActions && !canShowCommandActions) {
    return [null, null];
  }

  return [
    <Grid
      ref={gridRef}
      width="100%"
      height="min-content"
      key="maintenance-section"
      gap={0.25}
      backgroundColor="neutral.800"
      templateColumns="repeat(3, 1fr)"
    >
      {canShowMaintenanceActions && specificMaintenanceActions}
      {canShowCommandActions && specificCommandActions}
      <For each={fillerKeys}>{(key) => <GridItem key={key} backgroundColor="neutral.700" />}</For>
    </Grid>,
    null,
  ];
}

export const maintenanceAndCommandSection: [string, SensorSectionFactory<SensorOrSubSensorTypeEnum>] = [
  'common.maintenanceAndCommand',
  MaintenanceAndCommandSection,
];
